import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/*
 * Shared primitives for the Metrics pages.
 *
 * Styling matches the design prototype: pill scores with a colour ring, arrow
 * deltas, badge ticks, navy headings on a lavender accent.
 *
 * The "How this is calculated" explanations sit behind a `?` affordance rather
 * than inline — inline they took roughly a third of the vertical space on every
 * card. The text is kept because it is what stops these numbers being misread;
 * it just no longer dominates the layout.
 */

/* ---------------------------------------------------------------- *
 * formatting
 * ---------------------------------------------------------------- */

export const fmtDate = (ms) =>
  ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export const fmtMins = (ms) => {
  if (ms == null) return '—';
  const m = Math.round(ms / 60000);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

export const ago = (ms) => {
  if (!ms) return 'never';
  const days = Math.floor((Date.now() - ms) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  // One decimal, as the prototype had it: "1y ago" reads the same for 12 months
  // and 23, which matters when the number is being used to spot dormancy.
  return `${(days / 365).toFixed(1)}y ago`;
};

/** Minutes -> "1h 23m". The activity API reports minutes; fmtMins takes ms. */
export const fmtMinsFrom = (minutes) => (minutes == null ? '—' : fmtMins(minutes * 60000));


/* ---------------------------------------------------------------- *
 * popovers
 * ---------------------------------------------------------------- */

/**
 * Open-on-click state for a popover, with the two dismissals people expect:
 * clicking anywhere outside it, and Escape.
 *
 * Deliberately NOT hover. These panels sit directly over the controls beneath
 * them, so a popover that opened on hover covered the "View more details"
 * button the moment the pointer crossed the tile on its way there — the panel
 * appeared under the cursor and swallowed the click. Opening on click means
 * the panel is only ever in the way when it was asked for.
 *
 * Both refs are checked because the panel is rendered through a portal and so
 * is NOT a DOM descendant of the trigger: testing only the trigger would treat
 * every click inside the panel as an outside click and close it instantly.
 */
export function usePopover() {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      const inAnchor = anchorRef.current && anchorRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inAnchor && !inPanel) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return { open, setOpen, close, toggle, anchorRef, panelRef };
}

/**
 * The panel itself, rendered into document.body rather than next to its anchor.
 *
 * It has to escape its ancestors to be readable at all: the portfolio table
 * sits in a Card with `overflow-hidden` inside a horizontally scrolling div, so
 * a popover positioned inside that subtree is clipped at the card's edge — the
 * panel was being cut off at the bottom rather than overflowing the card as
 * intended. A portal plus fixed positioning means no ancestor can crop it.
 *
 * Position is measured from the anchor on open and recomputed on scroll and
 * resize, since fixed coordinates do not follow the page. It flips above the
 * anchor when there is more room up than down, clamps to the viewport
 * horizontally so a right-hand tile does not run off the edge, and caps its own
 * height with internal scrolling — a list of 75 organisations has to be
 * reachable, not merely rendered somewhere past the bottom of the screen.
 */
export function Popover({ anchorRef, panelRef, open, width = 320, className = '', children }) {
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const a = anchorRef.current;
      if (!a) return;
      const r = a.getBoundingClientRect();
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const below = vh - r.bottom - margin;
      const above = r.top - margin;
      // Flip only when down is genuinely cramped AND up is roomier, so the
      // panel keeps its usual place in the common case.
      const flipUp = below < 200 && above > below;

      const w = Math.min(width, vw - margin * 2);
      let left = r.left;
      if (left + w > vw - margin) left = vw - margin - w;
      if (left < margin) left = margin;

      setPos({
        left,
        width: w,
        maxHeight: Math.max(140, flipUp ? above : below),
        ...(flipUp ? { bottom: vh - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };

    place();
    window.addEventListener('resize', place);
    // Capture phase: the anchor may sit inside its own scrolling container, and
    // a scroll event there does not bubble to window.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef, width]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        bottom: pos.bottom,
        width: pos.width,
        maxHeight: pos.maxHeight,
        overflowY: 'auto',
        zIndex: 60,
      }}
      className={`rounded-lg border border-gray-200 bg-white p-3 text-xs font-normal leading-relaxed text-gray-600 shadow-lg ${className}`}
    >
      {children}
    </div>,
    document.body
  );
}

/* ---------------------------------------------------------------- *
 * "how this is calculated", behind a ?
 * ---------------------------------------------------------------- */

export function HowCalculated({ children, label = "How it's calculated", wide }) {
  const { open, toggle, anchorRef, panelRef } = usePopover();

  return (
    <span className="relative inline-block align-middle" ref={anchorRef}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={label}
        title={label}
        className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-[10px] font-semibold text-gray-400 hover:border-lavender hover:text-lavender focus:outline-none focus:ring-2 focus:ring-lavender/40"
      >
        ?
      </button>
      <Popover anchorRef={anchorRef} panelRef={panelRef} open={open} width={wide ? 420 : 320}>
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {label}
        </span>
        {children}
      </Popover>
    </span>
  );
}

/* ---------------------------------------------------------------- *
 * layout
 * ---------------------------------------------------------------- */

export function SectionHeading({ eyebrow, title, subtitle, help, right }) {
  return (
    <div className="mb-4 flex items-end justify-between gap-4">
      <div>
        {eyebrow && (
          <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-lavender">{eyebrow}</p>
        )}
        <h2 className="text-2xl font-bold text-navy">
          {title}
          {help && <HowCalculated>{help}</HowCalculated>}
        </h2>
        {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

/*
 * Table cell classes, copied from the prototype's .th / .td so the tables read
 * identically. Kept as constants rather than an @layer rule: the metrics page
 * should not add global CSS to an app that already has 20 stylesheets.
 */
export const TH = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

/*
 * Sticky header cells for a table inside its own scroll box.
 *
 * Deliberately applied to every `th` rather than to the `thead`, which is what
 * the prototype did. Tailwind's preflight sets `border-collapse: collapse`, and
 * with a collapsed table Safari does not honour `position: sticky` on `thead` —
 * the row breaks out of the scroll box and pins itself to the top of the page,
 * landing on top of the section tabs. Sticky on the cells is the form every
 * browser implements, so it stays inside its container.
 *
 * The background has to live here too: a sticky cell scrolls over the rows
 * beneath it, and without its own fill the text of row 12 shows through.
 */
export const TH_STICKY = 'sticky top-0 z-10 bg-gray-50';

/*
 * TD is deliberately split. In the prototype `.td` lived in @layer components,
 * so a cell could write `td text-gray-500` and the utility beat the layer. A JS
 * constant has no layer, so `${TD} text-gray-500` would leave two text-gray-*
 * utilities of equal specificity and Tailwind's own stylesheet order — not the
 * order written here — would pick the winner. TD_BASE therefore carries no
 * colour, for the cells that set their own.
 */
/*
 * TD_WRAP is TD_BASE without `whitespace-nowrap`, for the cells whose content
 * has to wrap — a long chapter title, mainly. It cannot be done as an override:
 * `whitespace-normal` and `whitespace-nowrap` are both plain utilities, and
 * Tailwind emits normal before nowrap, so nowrap wins whatever the order here.
 */
export const TD_WRAP = 'px-4 py-3 text-sm';
export const TD_BASE = `${TD_WRAP} whitespace-nowrap`;
export const TD = `${TD_BASE} text-gray-700`;

export function Card({ children, className = '' }) {
  return <div className={`rounded-lg bg-white p-6 shadow ${className}`}>{children}</div>;
}

export function StatTile({ label, value, unit, hint, help, tone = 'default' }) {
  const tones = {
    default: 'text-navy',
    good: 'text-emerald-600',
    warn: 'text-amber-600',
    accent: 'text-lavender',
  };
  return (
    <div className="min-w-[150px] flex-1 rounded-lg bg-white p-4 shadow">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
        {help && <HowCalculated>{help}</HowCalculated>}
      </p>
      <p className={`mt-1 text-2xl font-bold ${tones[tone]}`}>
        {value}
        {unit && <span className="ml-1 text-base font-semibold text-gray-400">{unit}</span>}
      </p>
      {hint && <p className="mt-0.5 text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * values
 * ---------------------------------------------------------------- */

/** Score pill, banded by colour. `null` is an em dash, never 0%. */
export function ScorePill({ pct, size = 'sm' }) {
  if (pct == null) return <span className="text-gray-300">—</span>;
  const v = Math.round(pct);
  const tone =
    v >= 80
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : v >= 60
        ? 'bg-amber-50 text-amber-700 ring-amber-200'
        : 'bg-rose-50 text-rose-700 ring-rose-200';
  const pad = size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs';
  return <span className={`inline-block rounded-full font-semibold tabular-nums ring-1 ${tone} ${pad}`}>{v}%</span>;
}

/**
 * Improvement in percentage points.
 *   null -> "n/a"       (not enough attempts to measure)
 *   0    -> "no change" (measured, and it genuinely did not move)
 */
export function Delta({ value, suffix = 'pts' }) {
  if (value == null) {
    return (
      <span className="text-xs text-gray-400" title="Needs at least 2 scored attempts to measure improvement">
        n/a
      </span>
    );
  }
  const rounded = Math.round(value);
  if (rounded === 0) return <span className="text-xs font-medium text-gray-400">no change</span>;
  const up = rounded > 0;
  return (
    <span className={`text-xs font-semibold tabular-nums ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
      {up ? '▲' : '▼'} {Math.abs(rounded)} {suffix}
    </span>
  );
}

/**
 * Tri-state badge. `null` renders as an em dash, NOT a cross — used where the
 * answer is unknowable rather than negative.
 */
export function Tick({ state, title }) {
  if (state === null || state === undefined) {
    return (
      <span
        title={title || 'Not yet known'}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-50 text-xs text-gray-300"
      >
        —
      </span>
    );
  }
  return state ? (
    <span
      title={title || 'Done'}
      className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700"
    >
      ✓
    </span>
  ) : (
    <span
      title={title || 'Not done'}
      className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-xs text-gray-300"
    >
      ○
    </span>
  );
}

export function Bar({ value, max = 100, className = '' }) {
  const p = max ? Math.round((100 * value) / max) : 0;
  return (
    <div className={`h-1.5 w-full overflow-hidden rounded-full bg-gray-100 ${className}`}>
      <div className="h-full rounded-full bg-lavender" style={{ width: `${p}%` }} />
    </div>
  );
}

/**
 * Score history as a tiny line, one marker per attempt.
 *
 * The y-axis is pinned to 0–100 rather than to the row's own min and max. Scaled
 * to its own range, a wobble between 71% and 73% draws the same dramatic climb as
 * a rise from 30% to 90%, and the column becomes actively misleading when you
 * read it down a table. Pinned, a shallow slope looks shallow.
 *
 * A single attempt draws a dot. Returning an em dash instead — as this used to —
 * hid the trend column for most learners, since one attempt is the common case.
 */
export function Sparkline({ points, width = 96, height = 26 }) {
  if (!points || points.length === 0) return <span className="text-xs text-gray-300">—</span>;
  if (points.length === 1) {
    return (
      <svg width={width} height={height} className="overflow-visible">
        <circle cx={width / 2} cy={height / 2} r="3" className="fill-lavender" />
      </svg>
    );
  }
  const min = Math.min(...points, 0);
  const max = Math.max(...points, 100);
  const x = (i) => (i / (points.length - 1)) * (width - 6) + 3;
  const y = (v) => height - 3 - ((v - min) / (max - min || 1)) * (height - 6);
  const d = points.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const rising = points[points.length - 1] >= points[0];
  return (
    <svg width={width} height={height} className="overflow-visible">
      <path
        d={d}
        fill="none"
        strokeWidth="1.75"
        className={rising ? 'stroke-emerald-500' : 'stroke-rose-400'}
      />
      {points.map((v, i) => (
        <circle
          key={i}
          cx={x(i)}
          cy={y(v)}
          r="1.9"
          className={rising ? 'fill-emerald-500' : 'fill-rose-400'}
        />
      ))}
    </svg>
  );
}

export function Chevron({ open }) {
  return (
    <svg
      className={`h-4 w-4 text-gray-400 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path d="M7.05 4.55a1 1 0 011.4 0l4.5 4.45a1 1 0 010 1.42l-4.5 4.45a1 1 0 11-1.4-1.42L10.83 10 7.05 6.27a1 1 0 010-1.72z" />
    </svg>
  );
}

/**
 * One AI avatar, colour-coded. The five personalities get five distinct colours
 * so a learner can track "how am I doing against Tony" across the page without
 * reading names — the same idea as a series colour in a chart.
 */
const AVATAR_COLORS = {
  Stefan: 'bg-indigo-500',
  AnneMarie: 'bg-rose-500',
  Ling: 'bg-teal-500',
  Tony: 'bg-amber-500',
  John: 'bg-sky-600',
};

export function AvatarChip({ name, size = 'sm', muted }) {
  const dim = size === 'lg' ? 'h-9 w-9 text-sm' : 'h-6 w-6 text-[10px]';
  return (
    <span
      title={muted ? `${name} — not faced yet` : name}
      className={`inline-flex ${dim} items-center justify-center rounded-full font-semibold text-white ${
        muted ? 'bg-gray-200 text-gray-400' : AVATAR_COLORS[name] || 'bg-gray-400'
      }`}
    >
      {name.slice(0, 2)}
    </span>
  );
}

/** Filter pills / segmented control, used for the section tabs and chapter filter. */
export function Pill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
        active ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {children}
    </button>
  );
}

/** Status chip for a chapter row. */
export const CHAPTER_STATUS = {
  complete: { label: 'Complete', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  'in-progress': { label: 'In progress', cls: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  'not-started': { label: 'Not started', cls: 'bg-gray-50 text-gray-500 ring-gray-200' },
};

export function StatusChip({ status }) {
  const s = CHAPTER_STATUS[status] || CHAPTER_STATUS['not-started'];
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${s.cls}`}>{s.label}</span>;
}

/**
 * Term on the left, meaning on the right.
 *
 * Used inside the `?` popovers wherever the thing being explained is a set of
 * labels — status bands, attention flags. A paragraph listing four definitions
 * in prose is read by nobody; a two-column table is scanned in a second.
 */
export function DefinitionTable({ title, rows }) {
  return (
    <span className="mb-2 block last:mb-0">
      {title && (
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {title}
        </span>
      )}
      <span className="block divide-y divide-gray-100 rounded border border-gray-100">
        {rows.map(([term, meaning]) => (
          <span key={term} className="flex gap-3 px-2 py-1.5">
            <span className="w-28 shrink-0 font-semibold text-navy">{term}</span>
            <span className="flex-1 text-gray-600">{meaning}</span>
          </span>
        ))}
      </span>
    </span>
  );
}

export function Empty({ children }) {
  return <p className="py-8 text-center text-sm italic text-gray-400">{children}</p>;
}

export function Loading({ what = 'metrics' }) {
  return (
    <div className="flex items-center justify-center py-16 text-gray-400">
      <span className="animate-pulse">Loading {what}…</span>
    </div>
  );
}

export function ErrorBox({ message, onRetry }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
      <p className="font-semibold">Could not load metrics</p>
      <p className="mt-1 text-rose-600">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="mt-2 rounded bg-white px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100">
          Try again
        </button>
      )}
    </div>
  );
}
